import React, { useEffect, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  TextField,
  InputAdornment,
  Pagination,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import Layout from "../components/layout/Layout";
import Panel from "../components/brand/Panel";
import KpiTile from "../components/brand/KpiTile";
import { globalSearch } from "../api/searchApi";

const PAGE_SIZE = 20;

const TYPE_LABELS = {
  unit: "Units",
  property: "Properties",
  ticket: "Tickets",
};

const TYPE_SINGULAR_LABELS = {
  unit: "Unit",
  property: "Property",
  ticket: "Ticket",
};

function SearchPage() {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const page = Math.max(Number(searchParams.get("page") || 1), 1);
  const selectedType = searchParams.get("type") || "";

  const [draft, setDraft] = useState(query);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState([]);
  const [counts, setCounts] = useState({ unit: 0, property: 0, ticket: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const getTypeLabel = (type, count) =>
    count === 1 ? TYPE_SINGULAR_LABELS[type] : TYPE_LABELS[type];

  useEffect(() => {
    setDraft(query);
  }, [query]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!query.trim()) {
        setResults([]);
        setCounts({ unit: 0, property: 0, ticket: 0 });
        setTotal(0);
        setTotalPages(0);
        setError("");
        return;
      }

      try {
        setLoading(true);
        setError("");
        const data = await globalSearch({
          q: query.trim(),
          page,
          page_size: PAGE_SIZE,
          type: selectedType || undefined,
        });
        if (cancelled) return;
        setResults(data?.results || []);
        setCounts(data?.counts || { unit: 0, property: 0, ticket: 0 });
        setTotal(data?.total || 0);
        setTotalPages(data?.total_pages || 0);
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        setError("Search failed. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [query, page, selectedType]);

  const groupedResults = results.reduce(
    (acc, item) => {
      acc[item.type] = [...(acc[item.type] || []), item];
      return acc;
    },
    { unit: [], property: [], ticket: [] }
  );

  const handleSearch = () => {
    const next = new URLSearchParams(searchParams);
    if (draft.trim()) {
      next.set("q", draft.trim());
      next.set("page", "1");
    } else {
      next.delete("q");
      next.delete("page");
      next.delete("type");
    }
    setSearchParams(next);
  };

  const handlePageChange = (event, value) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(value));
    setSearchParams(next);
  };

  const handleTypeFilter = (type) => {
    const next = new URLSearchParams(searchParams);
    if (type) {
      next.set("type", type);
      next.set("page", "1");
    } else {
      next.delete("type");
      next.set("page", "1");
    }
    setSearchParams(next);
  };

  const typeKpiConfig = [
    { type: "", label: "All results", sub: "Across types", value: total, active: !selectedType, alwaysClick: true },
    {
      type: "unit",
      label: "Units",
      sub: "Matching units",
      value: counts.unit,
      active: selectedType === "unit",
      alwaysClick: false,
    },
    {
      type: "property",
      label: "Properties",
      sub: "Matching properties",
      value: counts.property,
      active: selectedType === "property",
      alwaysClick: false,
    },
    {
      type: "ticket",
      label: "Tickets",
      sub: "Matching tickets",
      value: counts.ticket,
      active: selectedType === "ticket",
      alwaysClick: false,
    },
  ];

  return (
    <Layout eyebrow="Directory · Lookup" title="Search">
      <Typography sx={{ mb: 2.5, fontSize: 13, color: theme.palette.brand.inkMuted, maxWidth: 640 }}>
        Search across units, properties, and tickets. Use the tiles below to narrow by type after you run a search.
      </Typography>

      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          flexWrap: "wrap",
          alignItems: "stretch",
          mb: 3,
          p: 2,
          borderRadius: "8px",
          border: `1px solid ${theme.palette.brand.border}`,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <TextField
          size="small"
          fullWidth
          placeholder="Search…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSearch();
          }}
          sx={{
            flex: "1 1 320px",
            "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: 12.5 },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.brand.inkSoft, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          variant="contained"
          size="small"
          startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
          onClick={handleSearch}
          sx={{ px: 1.75, py: "8px", fontSize: 12.5, alignSelf: "center" }}
        >
          Search
        </Button>
      </Box>

      {query ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
            gap: 1.5,
            mb: 3,
          }}
        >
          {typeKpiConfig.map((row) => (
            <KpiTile
              key={row.type || "all"}
              label={row.label}
              value={loading && query ? "—" : row.value}
              sub={row.sub}
              active={row.active}
              onClick={
                row.alwaysClick || (row.type && counts[row.type] > 0)
                  ? () => handleTypeFilter(row.type)
                  : undefined
              }
            />
          ))}
        </Box>
      ) : null}

      {error ? (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: "8px",
            border: `1px solid ${theme.palette.brand.border}`,
            fontSize: 13,
          }}
        >
          {error}
        </Alert>
      ) : null}

      {!query ? (
        <Panel eyebrow="Start here" title="Run a search">
          <Box sx={{ p: 3, color: theme.palette.brand.inkSoft, fontSize: 13 }}>
            Enter a term above and click Search (or press Enter).
          </Box>
        </Panel>
      ) : null}

      {query && !loading && total === 0 && !error ? (
        <Panel eyebrow="No matches" title={`“${query}”`}>
          <Box sx={{ p: 3, color: theme.palette.brand.inkSoft, fontSize: 13 }}>
            Try a different keyword or clear the type filter.
          </Box>
        </Panel>
      ) : null}

      {loading && query ? (
        <Panel eyebrow="Working" title="Searching…">
          <Box sx={{ p: 3, color: theme.palette.brand.inkSoft, fontSize: 13 }}>Fetching results.</Box>
        </Panel>
      ) : null}

      {!loading && total > 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
          {["unit", "property", "ticket"].map((type) =>
            groupedResults[type]?.length ? (
              <Panel
                key={type}
                eyebrow={TYPE_LABELS[type]}
                title={`${groupedResults[type].length} ${getTypeLabel(type, groupedResults[type].length).toLowerCase()}`}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  {groupedResults[type].map((result, i) => (
                    <Box
                      key={`${result.type}-${result.id}`}
                      component={RouterLink}
                      to={result.url}
                      sx={{
                        textDecoration: "none",
                        color: "inherit",
                        px: 2.75,
                        py: 1.75,
                        borderBottom:
                          i < groupedResults[type].length - 1
                            ? `1px solid ${theme.palette.brand.border}`
                            : "none",
                        display: "block",
                        transition: "background .12s",
                        "&:hover": { backgroundColor: theme.palette.brand.surfaceMuted },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: 14, color: theme.palette.brand.ink }}>
                        {result.title}
                      </Typography>
                      {result.subtitle ? (
                        <Typography sx={{ mt: 0.5, fontSize: 12.5, color: theme.palette.brand.inkMuted }}>
                          {result.subtitle}
                        </Typography>
                      ) : null}
                      {result.meta ? (
                        <Typography sx={{ mt: 0.25, fontSize: 12, color: theme.palette.brand.inkSoft }}>
                          {result.meta}
                        </Typography>
                      ) : null}
                    </Box>
                  ))}
                </Box>
              </Panel>
            ) : null
          )}

          {totalPages > 1 ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
              <Pagination count={totalPages} page={page} onChange={handlePageChange} size="small" />
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Layout>
  );
}

export default SearchPage;
